import cn from "classnames"
import { useEffect, useState } from "react"

import { Button, Flex, message, Spin } from "@/atoms"
import { RESOURCES_NAMES } from "@/const/resource.constants"
import * as IssueDomain from "@/domain/issue"
import { shallowEqualObjects } from "@/helpers/utils"
import { useHttp } from "@/hooks/useHttp"
import { useNumberConvertor } from "@/hooks/useNumberConvertor"
import { usePrevious } from "@/hooks/usePrevious"
import { useTranslation } from "@/hooks/useTranslation"
import { IconDismissedTag } from "@/molecules/DismissedTag"
import { Protected } from "@/molecules/Protected"
import { LabelModal } from "@/organisms/LabelModal"
import {
  actions,
  useSelection
} from "@/pages/TriagePage/application/useSelection"
import {
  useDismissIssues,
  useDismissIssuesByFilter
} from "@/pages/TriagePage/application/useTriage"
import { useDismissModal } from "@/pages/TriagePage/hooks/useDismissModal"
import { DismissModal } from "../DismissModal"
import { IssueDismissInfo } from "../DismissModal/IssueDismissInfo"
import {
  getInitialState,
  getIssuesForDismissByProject,
  getIssuesForReopenByProject,
  getStateFromSelectedIssues
} from "./helpers"

import styles from "./IssueActions.module.scss"

import type { Issue } from "@/domain/issue"
import type { Permission } from "@/domain/user"
import type { IssueActionsComponent } from "."

export const IssueActions: IssueActionsComponent = (props) => {
  const { t } = useTranslation()
  const {
    params,
    componentName,
    showSelectedProjects,
    resourceId,
    totalIssuesCount = 0,
    totalProjectsCount = 0
  } = props
  const [selectedState, dispatch] = useSelection<Issue>()
  const [messageApi, messageContextHolder] = message.useMessage()
  const [{ dismissIssues, reopenIssues }] = useDismissIssues(
    params,
    { messageApi, showLoadingMessage: props.showLoadingMessage, componentName },
    props.getOptionsUseDismissIssues
  )

  const { selectedAll, params: paramsState } = selectedState
  const nextParams = { ...params, ...paramsState }
  const countContext = useHttp<{ data: number }>(
    `${RESOURCES_NAMES.TRIAGE.ISSUES_COUNT}`,
    nextParams,
    { enabled: selectedAll }
  )
  const count = countContext?.response?.data || 0
  const totalIssues = selectedAll ? count : totalIssuesCount
  const attr = { totalIssues, componentName, resourceId }

  const { dismissIssuesUpdater, reopenIssuesUpdater } =
    useDismissIssuesByFilter(attr, nextParams)

  const dismissAction = (attr?: { reason?: string }) => {
    if (selectedState.selectedAll) {
      dismissIssuesUpdater(attr?.reason)
    } else {
      dismissIssues(getIssuesForDismissByProject(selectedState.items), attr)
    }
  }

  const reopenAction = () => {
    if (selectedState.selectedAll) {
      reopenIssuesUpdater()
    } else {
      reopenIssues(getIssuesForReopenByProject(selectedState.items))
    }
  }

  const writePermission: Permission = { resource: "issues", action: "write" }
  const [state, setState] = useState(getInitialState())
  const { open, openModal, onOk, onCancel } = useDismissModal({
    onOk: dismissAction
  })

  useEffect(() => {
    const nextState = getStateFromSelectedIssues(selectedState)

    if (nextState) setState(nextState)
  }, [selectedState])

  const prevParams = usePrevious(params)
  const onFinishLabels = () => dispatch(actions.unselectAll())
  const { intlNumber } = useNumberConvertor()

  useEffect(() => {
    if (shallowEqualObjects(prevParams, params)) return

    dispatch(actions.setFlags({ unselect: true }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevParams, params])

  const { issuesCount, issuesTotal, reOpen, dismiss, projectsCount } = state
  const isSelectAllAvailable =
    !!issuesCount &&
    (issuesCount < totalIssuesCount || projectsCount < totalProjectsCount)

  const displayIssueCount = (actualSelection: number) =>
    selectedState.selectedAll ? totalIssuesCount : actualSelection

  const displayProjectsCount = () =>
    selectedState.selectedAll ? totalProjectsCount : projectsCount

  const countSelectedIssues = selectedState.selectedAll
    ? count
    : displayIssueCount(issuesCount)
  let issuesSelectedText = `${t("triage.issue_selected", {
    count: countSelectedIssues,
    selected: countContext.isLoading ? "..." : intlNumber(countSelectedIssues),
    total: intlNumber(displayIssueCount(issuesTotal))
  })}`

  const projectCount = displayProjectsCount()

  if (showSelectedProjects) {
    issuesSelectedText += ` ${t("triage.from_projects", { count: projectCount, selected: intlNumber(projectCount) })}`
  }

  const showActions = !!(issuesCount || selectedAll)

  const issueStatusFilterKey = "issueStatus"
  const issueStatusFilterValue: string =
    typeof params[issueStatusFilterKey] === "string"
      ? params[issueStatusFilterKey]
      : ""
  const isIssueStatusSelected = !!issueStatusFilterValue
  const isDismissedSelected =
    isIssueStatusSelected &&
    issueStatusFilterValue.includes(IssueDomain.STATUS.DISMISSED)
  const isOpenSelected =
    isIssueStatusSelected &&
    issueStatusFilterValue.includes(IssueDomain.STATUS.OPEN)
  const showDismissAction =
    dismiss || (selectedAll && (!isIssueStatusSelected || isOpenSelected))
  const showReopenAction =
    reOpen || (selectedAll && (!isIssueStatusSelected || isDismissedSelected))
  const { allocatableIssueCount } = state
  const isSelectedAllIssuesInProjects = allocatableIssueCount > 0 && !resourceId

  return (
    <div
      className={cn(
        styles.container,
        isSelectedAllIssuesInProjects &&
          "selected-all-available-issue-actions-ks"
      )}
    >
      {showActions && (
        <>
          <Flex vertical align="flex-start" wrap="nowrap">
            <span
              className={cn(isSelectAllAvailable && styles.issuesSelectedText)}
            >
              <Flex gap={8} align="center">
                {countContext.isLoading && (
                  <Spin spinning size="small" className={styles.spin} />
                )}
                {issuesSelectedText}
              </Flex>
            </span>
            {isSelectedAllIssuesInProjects && (
              <Button
                size="small"
                type="link"
                onClick={() => dispatch(actions.selectAllAllocatableIssues())}
                className={styles.selectAllContainer}
              >
                <span className={styles.selectAllText}>
                  {t("triage.selectAllIssuesInProjects", {
                    countIssues: intlNumber(allocatableIssueCount),
                    issues: t("general.issue", {
                      count: allocatableIssueCount
                    }),
                    projects: t("triage.this_projects", {
                      count: projectCount
                    })
                  })}
                </span>
              </Button>
            )}
          </Flex>
          <div className={styles.wrapButtons}>
            {showDismissAction && (
              <Protected permission={writePermission} shouldDisable>
                <Button
                  disabled={selectedState.updating || countContext.isLoading}
                  size="small"
                  type="secondary"
                  onClick={openModal}
                  icon={<IconDismissedTag />}
                >
                  {t("general.dismiss")}
                </Button>
              </Protected>
            )}
            {showReopenAction && (
              <Protected permission={writePermission} shouldDisable>
                <Button
                  disabled={selectedState.updating || countContext.isLoading}
                  size="small"
                  type="secondary"
                  icon={<IconDismissedTag />}
                  onClick={reopenAction}
                >
                  {t("general.reopen")}
                </Button>
              </Protected>
            )}
          </div>
        </>
      )}
      <DismissModal
        open={open}
        onOk={onOk}
        onCancel={onCancel}
        status="dismiss"
      >
        <IssueDismissInfo count={issuesCount} entityType="issue" />
      </DismissModal>
      {!!issuesCount && !selectedAll && (
        <LabelModal
          btnType="secondary"
          items={selectedState.items}
          onFinish={onFinishLabels}
          placement="tab"
          component={componentName || "issue-actions-component"}
        />
      )}
      {messageContextHolder}
    </div>
  )
}
